import { useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProjectStore } from '@/shared/stores/projectStore';
import { registerScene, getGame } from '@/shared/game/main';
import { Phase4Scene } from '../systems/Phase4Scene';
import { mapTDBattleResult } from '../utils/result-mapping';
import { calculateSettlement, applySettlementToPlayer } from '@/shared/formulas/settlement';
import type { TDBattleResult } from '../types/internal';

const PHASE4_SCENE_KEY = 'Phase4Scene';

interface MaintenanceLayoutProps {
  /** When embedded in hub, projectId is passed as prop instead of URL params */
  embeddedProjectId?: string;
  /** Called when maintenance is complete (embedded mode) */
  onComplete?: () => void;
  /** Called to exit maintenance early (embedded mode) */
  onExit?: () => void;
}

export function MaintenanceLayout({ embeddedProjectId, onComplete, onExit }: MaintenanceLayoutProps) {
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projectId = embeddedProjectId ?? routeProjectId;
  const isEmbedded = !!embeddedProjectId;
  const project = useProjectStore((s) => projectId ? s.projects[projectId] : undefined);
  const sceneStarted = useRef(false);
  const listenersAttached = useRef(false);
  const layoutRef = useRef<HTMLDivElement>(null);

  // Memoize validity check to avoid depending on full project object
  const isValid = !!project && !!project.battleResult && project.maintenanceResult === null;

  // When embedded, reposition #phaser-container above hub background,
  // clip hub bg to left side only so the character stays visible.
  useEffect(() => {
    if (!isEmbedded) return;
    const phaserContainer = document.getElementById('phaser-container');
    const reactUi = document.getElementById('react-ui');
    const hubBg = document.querySelector('.main-hub__background') as HTMLElement | null;
    const mainHub = document.querySelector('.main-hub') as HTMLElement | null;
    if (!phaserContainer || !layoutRef.current) return;

    const savedPhaserCss = phaserContainer.style.cssText;
    const savedReactUiZIndex = reactUi?.style.zIndex ?? '';
    const savedHubBgClip = hubBg?.style.clipPath ?? '';
    const savedMainHubBg = mainHub?.style.background ?? '';

    // Raise react-ui above canvas so the character portrait stays visible
    if (reactUi) reactUi.style.zIndex = '30';
    if (mainHub) mainHub.style.background = 'transparent';

    const sync = () => {
      if (!layoutRef.current) return;
      const rect = layoutRef.current.getBoundingClientRect();
      phaserContainer.style.position = 'fixed';
      phaserContainer.style.top = `${rect.top}px`;
      phaserContainer.style.left = `${rect.left}px`;
      phaserContainer.style.width = `${rect.width}px`;
      phaserContainer.style.height = `${rect.height}px`;
      phaserContainer.style.zIndex = '20';

      // Clip hub background to only show OUTSIDE the game area (keep left side visible)
      if (hubBg) {
        hubBg.style.clipPath = `polygon(0 0, ${rect.left}px 0, ${rect.left}px 100%, 0 100%)`;
      }
    };

    requestAnimationFrame(sync);
    const ro = new ResizeObserver(sync);
    ro.observe(layoutRef.current);

    return () => {
      ro.disconnect();
      phaserContainer.style.cssText = savedPhaserCss;
      if (reactUi) reactUi.style.zIndex = savedReactUiZIndex;
      if (hubBg) hubBg.style.clipPath = savedHubBgClip;
      if (mainHub) mainHub.style.background = savedMainHubBg;
    };
  }, [isEmbedded]);

  // Redirect if preconditions not met (route mode only)
  useEffect(() => {
    if (isEmbedded) return;
    if (!projectId || !project) {
      navigate('/hub', { replace: true });
      return;
    }
    if (project.maintenanceResult !== null) {
      navigate('/hub', { replace: true });
      return;
    }
    if (!project.battleResult) {
      navigate('/hub', { replace: true });
    }
  }, [projectId]); // Only check on mount/projectId change, not on project updates

  // Handle tdComplete callback - settlement + navigation
  const handleTdComplete = useCallback((tdResult: TDBattleResult) => {
    if (!projectId) return;

    const maintenanceResult = mapTDBattleResult(tdResult);
    useProjectStore.getState().completeMaintenance(projectId, maintenanceResult);

    // Settlement: calculate rewards → write to projectStore → apply to playerStore
    const proj = useProjectStore.getState().getProject(projectId);
    if (proj?.contractResult && proj?.battleResult) {
      const settlementResult = calculateSettlement(
        proj.contractResult,
        proj.battleResult,
        maintenanceResult,
      );
      useProjectStore.getState().completeSettlement(projectId, settlementResult);
      applySettlementToPlayer(settlementResult.economyDelta);
    }

    if (isEmbedded && onComplete) {
      onComplete();
    } else {
      navigate('/hub');
    }
  }, [projectId, navigate, isEmbedded, onComplete]);

  // Handle exit button (quit without completing)
  const handleExitToHub = useCallback(() => {
    if (isEmbedded && onExit) {
      onExit();
    } else {
      navigate('/hub');
    }
  }, [navigate, isEmbedded, onExit]);

  // Register and start Phase4Scene
  useEffect(() => {
    if (!isValid || sceneStarted.current) return;

    const game = getGame();
    if (!game) return;

    registerScene(PHASE4_SCENE_KEY, Phase4Scene);

    const existingScene = game.scene.getScene(PHASE4_SCENE_KEY);
    if (existingScene) {
      if (!game.scene.isActive(PHASE4_SCENE_KEY)) {
        game.scene.start(PHASE4_SCENE_KEY, {
          project: {
            id: project!.projectId,
            name: project!.projectName,
            region: undefined,
          },
        });
      }
    } else {
      game.scene.start(PHASE4_SCENE_KEY, {
        project: {
          id: project!.projectId,
          name: project!.projectName,
          region: undefined,
        },
      });
    }

    sceneStarted.current = true;

    // Attach event listeners on the scene's reactEvents emitter
    const scene = game.scene.getScene(PHASE4_SCENE_KEY) as Phase4Scene;
    if (scene) {
      scene.reactEvents.on('tdComplete', handleTdComplete);
      scene.reactEvents.on('exitToHub', handleExitToHub);
      listenersAttached.current = true;
    }

    return () => {
      // Clean up event listeners
      const currentGame = getGame();
      if (currentGame) {
        const currentScene = currentGame.scene.getScene(PHASE4_SCENE_KEY) as Phase4Scene | null;
        if (currentScene) {
          currentScene.reactEvents.off('tdComplete', handleTdComplete);
          currentScene.reactEvents.off('exitToHub', handleExitToHub);
          if (currentGame.scene.isActive(PHASE4_SCENE_KEY)) {
            currentGame.scene.stop(PHASE4_SCENE_KEY);
          }
        }
      }
      sceneStarted.current = false;
      listenersAttached.current = false;
    };
  }, [isValid, handleTdComplete, handleExitToHub]);

  if (!isValid) {
    return null;
  }

  return (
    <div ref={layoutRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Phase4Scene renders in the shared Phaser canvas */}
    </div>
  );
}
